import React, { useState, useEffect, useRef } from 'react';
import {
  X, Edit, KeyRound, Upload, Trash2, Loader, Image as ImageIcon, Link2,
} from 'lucide-react';
import api from '../../services/api';
import PhoneInput from '../../components/common/PhoneInput';
import { useAuth } from '../../context/AuthContext';
import { resolveUploadUrl, initiales } from '../../utils/media';
import toast from 'react-hot-toast';

// Palette étendue de couleurs de fond — tons sombres riches, harmonieux
// avec une sidebar en thème dark, regroupés par famille.
const COULEURS_FOND = [
  // Bleus marines / océan
  '#1e3a8a', '#1e40af', '#1d4ed8', '#172554',
  '#0c4a6e', '#075985', '#082f49', '#0e7490',
  // Slates / charcoals
  '#0f172a', '#1e293b', '#334155', '#020617',
  '#111827', '#1f2937', '#374151', '#030712',
  // Indigos / violets
  '#312e81', '#3730a3', '#4c1d95', '#581c87',
  '#6b21a8', '#86198f', '#701a75', '#4a044e',
  // Forêts / émeraudes
  '#064e3b', '#065f46', '#022c22', '#14532d',
  '#166534', '#365314',
  // Rouges sombres / bordeaux
  '#7f1d1d', '#831843', '#9f1239', '#4c0519',
  '#7c2d12', '#9a3412', '#451a03', '#78350f',
];

// Palette étendue de couleurs d'icônes — tons vifs et neutres lisibles
// sur fond sombre comme sur fond clair.
const COULEURS_ICONES = [
  // Gris / ardoise (sobre)
  '#64748b', '#94a3b8', '#cbd5e1', '#e2e8f0',
  '#475569', '#334155',
  // Ambres / dorés
  '#fbbf24', '#f59e0b', '#facc15', '#eab308', '#ca8a04',
  // Verts / émeraudes
  '#34d399', '#10b981', '#22c55e', '#84cc16', '#a3e635',
  // Bleus / cyans
  '#60a5fa', '#3b82f6', '#0ea5e9', '#06b6d4', '#22d3ee',
  // Roses / fuchsias
  '#f472b6', '#ec4899', '#e879f9', '#d946ef',
  // Oranges
  '#fb923c', '#f97316', '#ea580c',
  // Violets / mauves
  '#a78bfa', '#8b5cf6', '#7c3aed', '#a855f7',
  // Rouges
  '#f87171', '#ef4444', '#dc2626',
];
const DEVISES = ['GNF', 'XOF', 'EUR', 'USD'];
const LANGUES = [{ code: 'fr', nom: 'Français' }, { code: 'en', nom: 'English' }];

function urlLogoAbs(logo_url) {
  if (!logo_url) return null;
  if (logo_url.startsWith('http')) return logo_url;
  if (logo_url.startsWith('/uploads/')) {
    const base = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api')
      .replace(/\/api\/?$/, '');
    return base + logo_url;
  }
  return logo_url;
}

// ── Helpers ──────────────────────────────────────────────────
function Alert({ type, msg }) {
  if (!msg) return null;
  const s = type === 'success'
    ? 'bg-green-900/20 border-green-300 text-green-400'
    : 'bg-red-900/20 border-red-300 text-red-400';
  return <div className={`border rounded-lg px-4 py-3 text-sm ${s}`}>{msg}</div>;
}

function InputField({ label, name, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-1">{label}</label>
      <input
        type={type}
        name={name}
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 transition-colors"
      />
    </div>
  );
}

// ── Onglet Infos Entreprise ───────────────────────────────────
// Combine :
//   - /api/mon-commerce  (nom, logo_url, couleur_principale, contact, devise…)
//   - /api/parametres    (clé/valeur — pour message_recu et autres legacy)
function OngletEntreprise() {
  const { refreshUser, couleurIcones } = useAuth();
  const [commerce, setCommerce] = useState({});
  const [parametres, setParametres] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [alert, setAlert] = useState({ type: '', msg: '' });
  const fileInputRef = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get('/mon-commerce').then(r => r.data).catch(() => null),
      api.get('/parametres').then(r => r.data).catch(() => ({})),
    ]).then(([c, p]) => {
      if (c) setCommerce(c);
      setParametres(p || {});
    }).finally(() => setLoading(false));
  }, []);

  const setC = (k, v) => setCommerce(f => ({ ...f, [k]: v }));
  const setP = (k, v) => setParametres(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    setAlert({ type: '', msg: '' });
    try {
      // Sauvegarder l'identité du commerce (champs modifiés uniquement)
      const champsC = ['nom', 'logo_url',
        'couleur_principale', 'couleur_secondaire',
        'adresse', 'ville', 'pays', 'telephone', 'email',
        'devise', 'langue', 'type_activite'];
      const payloadC = {};
      champsC.forEach(k => {
        if (commerce[k] !== undefined) payloadC[k] = commerce[k] || null;
      });
      if (Object.keys(payloadC).length > 0) {
        await api.put('/mon-commerce', payloadC);
      }
      // Sauvegarder les paramètres legacy (message_recu, etc.)
      if (Object.keys(parametres).length > 0) {
        await api.put('/parametres', parametres);
      }
      setAlert({ type: 'success', msg: 'Paramètres enregistrés' });
      if (refreshUser) refreshUser().catch(() => {});
    } catch (err) {
      setAlert({ type: 'error', msg: err.response?.data?.message || 'Erreur lors de la sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  const handleUploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (2 Mo max)');
      e.target.value = '';
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('logo', f);
      const { data: updated } = await api.post('/mon-commerce/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setC('logo_url', updated.logo_url);
      toast.success('Logo téléversé');
      if (refreshUser) refreshUser().catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.message || 'Échec');
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm('Supprimer le logo actuel ?')) return;
    try {
      await api.delete('/mon-commerce/logo');
      setC('logo_url', null);
      toast.success('Logo supprimé');
      if (refreshUser) refreshUser().catch(() => {});
    } catch {
      toast.error('Échec');
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-400">Chargement…</div>;

  return (
    <div className="max-w-3xl space-y-6">
      <Alert type={alert.type} msg={alert.msg} />

      {/* ── Identité ─────────────────────────────────────── */}
      <section className="bg-gray-900/40 border border-gray-800 rounded-xl p-5 space-y-4">
        <h3 className="text-sm font-semibold text-white">Identité</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField label="Nom du commerce *" name="nom"
            value={commerce.nom} onChange={e => setC('nom', e.target.value)}
            placeholder="Nom de l'entreprise" />
          <InputField label="Type d'activité" name="type_activite"
            value={commerce.type_activite} onChange={e => setC('type_activite', e.target.value)}
            placeholder="Mode et accessoires" />
        </div>
      </section>

      {/* ── Apparence ────────────────────────────────────── */}
      <section className="bg-gray-900/40 border border-gray-800 rounded-xl p-5 space-y-4">
        <h3 className="text-sm font-semibold text-white">Apparence</h3>

        {/* Logo */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Logo</label>
          <div className="flex items-start gap-4">
            <div className="w-24 h-24 bg-gray-800 border-2 border-dashed border-gray-700 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
              {commerce.logo_url ? (
                <img src={urlLogoAbs(commerce.logo_url)} alt="logo"
                  className="w-full h-full object-contain"
                  onError={e => { e.target.style.display = 'none'; }} />
              ) : (
                <ImageIcon size={28} className="text-gray-600" />
              )}
            </div>
            <div className="flex-1 space-y-2">
              <input ref={fileInputRef} type="file" className="hidden"
                accept=".png,.jpg,.jpeg,.webp,.svg,.gif"
                onChange={handleUploadLogo} />
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-500 hover:bg-blue-600
                             disabled:opacity-40 text-white text-sm rounded">
                  {uploadingLogo ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
                  Téléverser
                </button>
                {commerce.logo_url && (
                  <button type="button" onClick={handleRemoveLogo}
                    className="flex items-center gap-2 px-3 py-1.5 bg-red-500/15 hover:bg-red-500/25
                               text-red-300 text-sm rounded">
                    <Trash2 size={14} /> Retirer
                  </button>
                )}
              </div>
              <details className="text-xs text-gray-400">
                <summary className="cursor-pointer hover:text-gray-300 flex items-center gap-1">
                  <Link2 size={12} /> Ou coller un lien public
                </summary>
                <input type="url"
                  value={commerce.logo_url && commerce.logo_url.startsWith('http') ? commerce.logo_url : ''}
                  onChange={e => setC('logo_url', e.target.value)}
                  placeholder="https://…/mon-logo.png"
                  className="w-full mt-2 bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white" />
              </details>
              <p className="text-xs text-gray-500">PNG, JPG, WEBP, SVG ou GIF, ≤ 2 Mo.</p>
            </div>
          </div>
        </div>

        {/* Couleur de fond (sidebar / header) */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Couleur de fond</label>
          <p className="text-xs text-gray-500 mb-2">
            Couleur dominante de la barre latérale et du fond. Le texte par-dessus
            s'adapte automatiquement en blanc ou noir selon la luminosité.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input type="color" value={commerce.couleur_principale || '#1e3a8a'}
              onChange={e => setC('couleur_principale', e.target.value)}
              className="w-12 h-10 bg-transparent border border-gray-700 rounded cursor-pointer" />
            <input type="text" value={commerce.couleur_principale || ''}
              onChange={e => setC('couleur_principale', e.target.value)}
              placeholder="#1e3a8a"
              className="w-32 bg-gray-800 border border-gray-700 rounded px-2 py-2 text-sm text-white font-mono" />
          </div>
          {/* Grille de swatches étendue */}
          <div className="grid grid-cols-12 gap-1.5 mt-3 max-w-xl">
            {COULEURS_FOND.map(c => {
              const actif = (commerce.couleur_principale || '').toLowerCase() === c.toLowerCase();
              return (
                <button key={c} type="button" onClick={() => setC('couleur_principale', c)}
                  className={`w-7 h-7 rounded-md border-2 hover:scale-110 transition-transform ${
                    actif ? 'border-white ring-2 ring-white/40' : 'border-gray-700'
                  }`}
                  style={{ backgroundColor: c }} title={c} />
              );
            })}
          </div>
        </div>

        {/* Couleur des icônes (accents) */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Couleur des icônes</label>
          <p className="text-xs text-gray-500 mb-2">
            Couleur des icônes actives et des accents (boutons, badges).
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input type="color" value={commerce.couleur_secondaire || '#64748b'}
              onChange={e => setC('couleur_secondaire', e.target.value)}
              className="w-12 h-10 bg-transparent border border-gray-700 rounded cursor-pointer" />
            <input type="text" value={commerce.couleur_secondaire || ''}
              onChange={e => setC('couleur_secondaire', e.target.value)}
              placeholder="#64748b"
              className="w-32 bg-gray-800 border border-gray-700 rounded px-2 py-2 text-sm text-white font-mono" />
          </div>
          <div className="grid grid-cols-12 gap-1.5 mt-3 max-w-xl">
            {COULEURS_ICONES.map(c => {
              const actif = (commerce.couleur_secondaire || '').toLowerCase() === c.toLowerCase();
              return (
                <button key={c} type="button" onClick={() => setC('couleur_secondaire', c)}
                  className={`w-7 h-7 rounded-md border-2 hover:scale-110 transition-transform ${
                    actif ? 'border-white ring-2 ring-white/40' : 'border-gray-700'
                  }`}
                  style={{ backgroundColor: c }} title={c} />
              );
            })}
          </div>
        </div>

        {/* Aperçu live */}
        <div className="mt-2">
          <p className="text-xs text-gray-500 mb-2">Aperçu</p>
          <div className="rounded-lg p-3 flex items-center gap-3"
            style={{ backgroundColor: commerce.couleur_principale || '#1e3a8a' }}>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: (commerce.couleur_secondaire || '#64748b') + '40' }}>
              <span style={{ color: commerce.couleur_secondaire || '#64748b' }}>★</span>
            </div>
            <div>
              <p className="font-bold text-sm" style={{ color: (commerce.couleur_principale || '#1e3a8a')
                .toLowerCase().match(/#?[0-9a-f]{6}/) && parseInt((commerce.couleur_principale || '#1e3a8a').replace('#',''), 16) > 0x7f7f7f
                ? '#0f172a' : '#ffffff' }}>
                {commerce.nom || 'Nom du commerce'}
              </p>
              <p className="text-xs opacity-70" style={{ color: '#ffffff' }}>
                Aperçu sidebar
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Coordonnées ─────────────────────────────────── */}
      <section className="bg-gray-900/40 border border-gray-800 rounded-xl p-5 space-y-4">
        <h3 className="text-sm font-semibold text-white">Coordonnées</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <PhoneInput label="Téléphone" value={commerce.telephone}
            onChange={e => setC('telephone', e.target.value)} />
          <InputField label="Email" name="email" type="email"
            value={commerce.email} onChange={e => setC('email', e.target.value)}
            placeholder="email@gmail.com" />
          <InputField label="Adresse" name="adresse"
            value={commerce.adresse} onChange={e => setC('adresse', e.target.value)}
            placeholder="Rue 10" />
          <InputField label="Ville" name="ville"
            value={commerce.ville} onChange={e => setC('ville', e.target.value)}
            placeholder="Conakry" />
          <InputField label="Pays" name="pays"
            value={commerce.pays} onChange={e => setC('pays', e.target.value)}
            placeholder="Guinée" />
        </div>
      </section>

      {/* ── Régionalisation ─────────────────────────────── */}
      <section className="bg-gray-900/40 border border-gray-800 rounded-xl p-5 space-y-4">
        <h3 className="text-sm font-semibold text-white">Régionalisation</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Devise</label>
            <select value={commerce.devise || 'GNF'} onChange={e => setC('devise', e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white">
              {DEVISES.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Langue par défaut</label>
            <select value={commerce.langue || 'fr'} onChange={e => setC('langue', e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white">
              {LANGUES.map(l => <option key={l.code} value={l.code}>{l.nom}</option>)}
            </select>
          </div>
        </div>
      </section>

      {/* ── Messages sur reçus ──────────────────────────── */}
      <section className="bg-gray-900/40 border border-gray-800 rounded-xl p-5 space-y-3">
        <h3 className="text-sm font-semibold text-white">Tickets et reçus</h3>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Message sur les reçus</label>
          <textarea
            value={parametres.message_recu || ''}
            onChange={e => setP('message_recu', e.target.value)}
            rows={2}
            placeholder="Merci pour votre achat !"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 resize-none" />
        </div>
      </section>

      {/* ── Garde-fou rentabilité ──────────────────────────── */}
      <section className="bg-gray-900/40 border border-gray-800 rounded-xl p-5 space-y-3">
        <h3 className="text-sm font-semibold text-white">Garde-fou rentabilité</h3>

        {/* Interrupteur principal : active/désactive tout le contrôle */}
        <label className="flex items-start gap-3 cursor-pointer bg-gray-800/50 border border-gray-700 rounded-lg p-3">
          <input
            type="checkbox"
            checked={parametres.controle_marge_actif !== 'false'}
            onChange={e => setP('controle_marge_actif', e.target.checked ? 'true' : 'false')}
            className="w-5 h-5 mt-0.5 accent-yellow-500 flex-shrink-0"
          />
          <span>
            <span className="block text-sm font-medium text-gray-200">Activer le contrôle des marges</span>
            <span className="block text-xs text-gray-500 mt-0.5">
              Quand c'est activé : la marge s'affiche sur les prix et à la caisse, et toute vente
              (ou prix) <strong>sous le coût d'achat</strong> demande une confirmation. Décoche pour
              tout désactiver (le rapport « Rentabilité » reste consultable).
            </span>
          </span>
        </label>

        <div className={parametres.controle_marge_actif === 'false' ? 'opacity-50 pointer-events-none' : ''}>
          <label className="block text-sm font-medium text-gray-300 mb-1">Seuil de marge faible (%)</label>
          <input
            type="number" min="0" max="100"
            value={parametres.marge_seuil_alerte_pct ?? ''}
            onChange={e => setP('marge_seuil_alerte_pct', e.target.value)}
            placeholder="10"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500" />
          <p className="text-xs text-gray-500 mt-1">
            En dessous de cette marge (calculée sur le coût d'achat moyen), le prix d'un produit s'affiche en orange.
            Une vente <strong>sous le coût</strong> demande toujours une confirmation. Mets <strong>0</strong> pour désactiver l'alerte orange.
          </p>
        </div>
      </section>

      {/* ── Mode de rémunération des vendeurs terrain ──────── */}
      <section className="bg-gray-900/40 border border-gray-800 rounded-xl p-5 space-y-3">
        <h3 className="text-sm font-semibold text-white">Vendeurs terrain — mode de rémunération</h3>
        <label className="flex items-start gap-3 cursor-pointer bg-gray-800/50 border border-gray-700 rounded-lg p-3">
          <input
            type="checkbox"
            checked={parametres.mode_vendeur === 'commission'}
            onChange={e => setP('mode_vendeur', e.target.checked ? 'commission' : 'standard')}
            className="w-5 h-5 mt-0.5 accent-yellow-500 flex-shrink-0"
          />
          <span>
            <span className="block text-sm font-medium text-gray-200">Activer la gestion des commissions</span>
            <span className="block text-xs text-gray-500 mt-0.5">
              <strong>Désactivé (standard)</strong> : le vendeur remet 100 % de la valeur confiée,
              comme aujourd'hui. <strong>Activé</strong> : à la clôture du soir, tu saisis le prix réel
              de vente et une commission par article pour les vendeurs marqués « à la commission ».
              La commission leur est versée le soir (enregistrée comme dépense). Chaque vendeur reste
              réglable « au fixe » ou « à la commission » sur sa fiche.
            </span>
          </span>
        </label>
      </section>

      <button onClick={handleSave} disabled={saving}
        style={{ backgroundColor: couleurIcones || '#4f46e5' }}
        className="w-full text-white py-2.5 rounded-xl font-semibold transition hover:opacity-90 disabled:opacity-50">
        {saving ? 'Enregistrement…' : 'Enregistrer les modifications'}
      </button>
    </div>
  );
}

// ── Modal : modifier les infos d'un utilisateur ──────────────
function ModalEditerUtilisateur({ utilisateur, boutiques = [], onClose, onSuccess }) {
  const [form, setForm] = useState({
    prenom: utilisateur?.prenom || '',
    nom:    utilisateur?.nom    || '',
    email:  utilisateur?.email  || '',
    role:   utilisateur?.role   || 'gerant_vendeur',
    actif:  utilisateur?.actif !== false,
    boutique_id: utilisateur?.boutique_id || '',
  });
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');

  const roleAvecBoutique = form.role === 'gerant_vendeur' || form.role === 'vendeur';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (roleAvecBoutique && boutiques.length > 1 && !form.boutique_id) {
      return setErreur('Choisissez la boutique d\'affectation de ce gérant/vendeur.');
    }
    setSaving(true);
    setErreur('');
    try {
      await api.put(`/users/${utilisateur.id}`, {
        ...form,
        boutique_id: roleAvecBoutique ? (form.boutique_id || undefined) : null,
      });
      onSuccess();
    } catch (err) {
      setErreur(err.response?.data?.message || 'Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-gray-900 rounded-2xl w-full max-w-lg p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">Modifier l'utilisateur</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-300"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          {erreur && <Alert type="error" msg={erreur} />}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Prénom</label>
              <input value={form.prenom} onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))}
                className="w-full bg-gray-900 text-white border border-gray-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Nom</label>
              <input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                className="w-full bg-gray-900 text-white border border-gray-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
            <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="email@gmail.com"
              className="w-full bg-gray-900 text-white border border-gray-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Rôle</label>
            <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
              className="w-full bg-gray-900 text-white border border-gray-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none">
              <option value="gerant_vendeur">Gérant/Vendeur</option>
              <option value="vendeur">Vendeur terrain</option>
              <option value="proprietaire">Propriétaire</option>
              <option value="consultation">Consultation (lecture seule)</option>
              {/* Le rôle Admin est réservé au Super Admin. L'option n'apparaît
                  que si l'utilisateur édité est DÉJÀ admin (pour conserver son
                  rôle) — jamais comme promotion. */}
              {utilisateur?.role === 'admin' && <option value="admin">Admin</option>}
            </select>
            <p className="text-xs text-gray-600 mt-1 italic">
              Le rôle Admin ne peut être attribué que par le Super Admin.
            </p>
          </div>

          {/* Affectation boutique : une seule pour gérant/vendeur */}
          {roleAvecBoutique && boutiques.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Boutique d'affectation {boutiques.length > 1 && <span className="text-red-400">*</span>}
              </label>
              <select
                value={form.boutique_id}
                onChange={e => setForm(f => ({ ...f, boutique_id: e.target.value }))}
                className="w-full bg-gray-900 text-white border border-gray-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
              >
                <option value="">{boutiques.length === 1 ? `${boutiques[0].nom} (automatique)` : '— Choisir une boutique —'}</option>
                {boutiques.map(b => (
                  <option key={b.id} value={b.id}>{b.nom}{b.ville ? ` — ${b.ville}` : ''}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Un gérant/vendeur opère dans une seule boutique.</p>
            </div>
          )}
          {form.role === 'proprietaire' && boutiques.length > 1 && (
            <p className="text-xs text-emerald-400/80 bg-emerald-900/20 border border-emerald-700/40 rounded-lg px-3 py-2">
              Le propriétaire a automatiquement accès à toutes les boutiques ({boutiques.length}).
            </p>
          )}
          <div className="flex items-center gap-2 pt-1">
            <input type="checkbox" id="actif" checked={form.actif}
              onChange={e => setForm(f => ({ ...f, actif: e.target.checked }))}
              className="w-4 h-4 rounded border-gray-700 text-indigo-600 focus:ring-indigo-400" />
            <label htmlFor="actif" className="text-sm text-gray-300">Compte actif</label>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={saving}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm border hover:bg-gray-800">
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Modal : réinitialiser le mot de passe d'un utilisateur ──────
function ModalResetMotDePasse({ utilisateur, onClose, onSuccess }) {
  const [mdp1, setMdp1] = useState('');
  const [mdp2, setMdp2] = useState('');
  const [voir, setVoir] = useState(false);
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErreur('');
    if (mdp1.length < 6) {
      setErreur('Le mot de passe doit faire au moins 6 caractères');
      return;
    }
    if (mdp1 !== mdp2) {
      setErreur('Les deux saisies ne correspondent pas');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/users/${utilisateur.id}/reset-password`, { nouveauMotDePasse: mdp1 });
      onSuccess(`Mot de passe réinitialisé pour ${utilisateur.prenom} ${utilisateur.nom}`);
    } catch (err) {
      setErreur(err.response?.data?.message || 'Erreur lors de la réinitialisation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-gray-900 rounded-2xl w-full max-w-md p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">Réinitialiser le mot de passe</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-300"><X size={20} /></button>
        </div>
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-3 text-sm text-amber-300 mb-4">
          ⚠️ Tu vas définir un mot de passe <strong>temporaire</strong> pour
          <strong> {utilisateur.prenom} {utilisateur.nom}</strong> ({utilisateur.email}).
          <ul className="mt-1.5 ml-4 list-disc text-xs space-y-0.5">
            <li>Communique-le-lui en personne — l'app ne lui envoie pas d'email.</li>
            <li>À sa prochaine connexion, il sera <strong>obligé de le changer</strong> avant d'accéder à l'app.</li>
          </ul>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          {erreur && <Alert type="error" msg={erreur} />}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Nouveau mot de passe</label>
            <input type={voir ? 'text' : 'password'} value={mdp1} onChange={e => setMdp1(e.target.value)}
              placeholder="Min 6 caractères" autoFocus
              className="w-full bg-gray-900 text-white border border-gray-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Confirmer</label>
            <input type={voir ? 'text' : 'password'} value={mdp2} onChange={e => setMdp2(e.target.value)}
              placeholder="Re-saisis le mot de passe"
              className="w-full bg-gray-900 text-white border border-gray-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none" />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
            <input type="checkbox" checked={voir} onChange={e => setVoir(e.target.checked)}
              className="w-4 h-4 rounded border-gray-700 text-indigo-600 focus:ring-indigo-400" />
            Afficher les mots de passe
          </label>
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={saving}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
              {saving ? 'Réinitialisation…' : 'Réinitialiser'}
            </button>
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm border hover:bg-gray-800">
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Onglet Utilisateurs ───────────────────────────────────────
function OngletUtilisateurs() {
  const { isAdmin, user: userConnecte } = useAuth();
  const [users, setUsers] = useState([]);
  const [boutiques, setBoutiques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ prenom: '', nom: '', email: '', password: '', role: 'gerant_vendeur', boutique_id: '' });
  const [alert, setAlert] = useState({ type: '', msg: '' });
  const [saving, setSaving] = useState(false);
  const [userEdition, setUserEdition] = useState(null);     // user en cours d'édition
  const [userResetMdp, setUserResetMdp] = useState(null);   // user dont on réinitialise le mdp

  const load = () => {
    setLoading(true);
    api.get('/users')
      .then(r => setUsers(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // Boutiques actives du commerce (pour l'affectation des gérants/vendeurs)
    api.get('/boutiques')
      .then(r => setBoutiques((r.data || []).filter(b => b.statut === 'actif')))
      .catch(() => setBoutiques([]));
  }, []);

  // Rôles rattachés à UNE boutique (vs proprietaire/admin = vue globale)
  const roleAvecBoutique = (r) => r === 'gerant_vendeur' || r === 'vendeur';

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleCreate = async e => {
    e.preventDefault();
    // Affectation obligatoire si plusieurs boutiques et rôle boutique
    if (roleAvecBoutique(form.role) && boutiques.length > 1 && !form.boutique_id) {
      return setAlert({ type: 'error', msg: 'Choisissez la boutique d\'affectation de ce gérant/vendeur.' });
    }
    setSaving(true);
    setAlert({ type: '', msg: '' });
    try {
      await api.post('/users', {
        ...form,
        boutique_id: roleAvecBoutique(form.role) ? (form.boutique_id || undefined) : undefined,
      });
      setAlert({ type: 'success', msg: 'Utilisateur créé avec succès !' });
      setForm({ prenom: '', nom: '', email: '', password: '', role: 'gerant_vendeur', boutique_id: '' });
      setShowForm(false);
      load();
    } catch (err) {
      setAlert({ type: 'error', msg: err.response?.data?.message || 'Erreur lors de la création' });
    } finally {
      setSaving(false);
    }
  };

  // État pour le modal de confirmation. On stocke directement l'utilisateur
  // ciblé : null = pas de confirmation en attente, sinon { id, prenom, nom,
  // email, actif } à toggler.
  const [confirmToggle, setConfirmToggle] = useState(null);

  // Demande de toggle : on passe par une confirmation au lieu d'agir directement.
  const handleToggle = (u) => {
    setConfirmToggle(u);
  };

  const confirmerToggle = async () => {
    if (!confirmToggle) return;
    try {
      await api.put(`/users/${confirmToggle.id}`, { actif: !confirmToggle.actif });
      setAlert({
        type: 'success',
        msg: confirmToggle.actif
          ? `${confirmToggle.prenom} ${confirmToggle.nom} a été désactivé(e)`
          : `${confirmToggle.prenom} ${confirmToggle.nom} a été réactivé(e)`,
      });
      load();
    } catch (err) {
      setAlert({ type: 'error', msg: err.response?.data?.message || 'Erreur lors de la modification' });
    } finally {
      setConfirmToggle(null);
    }
  };

  // Définition des rôles : icône, libellé explicite, et palette de couleurs
  // pour la pastille (badge) affichée dans la liste.
  const roles = {
    admin: {
      icone: '',
      libelle: 'Admin',
      couleur: 'bg-red-100 text-red-400 border-red-700/50',
    },
    proprietaire: {
      icone: '',
      libelle: 'Propriétaire',
      couleur: 'bg-yellow-100 text-yellow-300 border-yellow-700/50',
    },
    gerant_vendeur: {
      icone: '',
      libelle: 'Gérant/Vendeur',
      couleur: 'bg-blue-100 text-blue-400 border-blue-700/50',
    },
    // Alias legacy : si une ligne en base remonte encore 'employe' (cas
    // d'une migration 023 non appliquée), on affiche un libellé propre.
    employe: {
      icone: '',
      libelle: 'Gérant/Vendeur',
      couleur: 'bg-blue-100 text-blue-400 border-blue-700/50',
    },
    vendeur: {
      icone: '',
      libelle: 'Vendeur terrain',
      couleur: 'bg-green-100 text-green-400 border-green-700/50',
    },
    consultation: {
      icone: '',
      libelle: 'Consultation',
      couleur: 'bg-slate-100 text-slate-400 border-slate-600/50',
    },
  };

  // Petit composant inline pour afficher proprement un rôle (icône + libellé)
  // partout où on en a besoin (tableau, formulaire, etc.).
  const RoleBadge = ({ role }) => {
    const r = roles[role];
    if (!r) {
      return <span className="text-xs text-gray-500">{role || '—'}</span>;
    }
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-semibold ${r.couleur}`}>
        <span>{r.libelle}</span>
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <Alert type={alert.type} msg={alert.msg} />

      {/* Bandeau d'info si l'utilisateur n'est pas admin */}
      {!isAdmin && (
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-3 text-sm text-amber-300">
          🔒 Seul un <strong>administrateur</strong> peut créer, modifier ou désactiver un utilisateur.
          Tu peux consulter la liste mais pas la modifier.
        </div>
      )}

      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">{users.length} utilisateur(s)</p>
        {isAdmin && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition"
          >
            + Nouvel utilisateur
          </button>
        )}
      </div>

      {showForm && isAdmin && (
        <form onSubmit={handleCreate} className="bg-indigo-900/30 border border-indigo-700/50 rounded-xl p-4 space-y-3 max-w-lg">
          <h3 className="font-semibold text-indigo-300 text-sm">Créer un utilisateur</h3>
          <div className="grid grid-cols-2 gap-3">
            <InputField label="Prénom" name="prenom" value={form.prenom} onChange={handleChange} />
            <InputField label="Nom" name="nom" value={form.nom} onChange={handleChange} />
          </div>
          <InputField label="Email" name="email" value={form.email} onChange={handleChange} type="email" placeholder="email@gmail.com" />
          <InputField label="Mot de passe temporaire" name="password" value={form.password} onChange={handleChange} type="password" />
          <p className="text-xs text-gray-500 -mt-2 italic">
            🔐 L'utilisateur devra le changer obligatoirement à sa première connexion.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Rôle</label>
            <select
              name="role"
              value={form.role}
              onChange={handleChange}
              className="w-full bg-gray-900 text-white border border-gray-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none"
            >
              <option value="gerant_vendeur">Gérant/Vendeur — opérations d'une boutique</option>
              <option value="vendeur">Vendeur terrain — sorties / retours (login bloqué)</option>
              <option value="proprietaire">Propriétaire — vue business (dashboard, finances)</option>
              <option value="consultation">Consultation — lecture seule sur tout le commerce</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {form.role === 'proprietaire' && 'Accès au dashboard, aux clôtures et aux rapports financiers.'}
              {form.role === 'gerant_vendeur' && 'Accès aux ventes, commandes, stock et produits de sa boutique.'}
              {form.role === 'vendeur'      && 'Accès terrain : sorties matin, retours soir.'}
              {form.role === 'consultation' && 'Voit tout le commerce (toutes boutiques, finances) en LECTURE SEULE — aucune modification possible.'}
            </p>
            <p className="text-xs text-gray-600 mt-1 italic">
              Le rôle Admin ne peut être attribué que par le Super Admin.
            </p>
          </div>

          {/* Affectation boutique : une seule pour gérant/vendeur, toutes pour propriétaire */}
          {roleAvecBoutique(form.role) && boutiques.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Boutique d'affectation {boutiques.length > 1 && <span className="text-red-400">*</span>}
              </label>
              <select
                value={form.boutique_id}
                onChange={e => setForm(f => ({ ...f, boutique_id: e.target.value }))}
                className="w-full bg-gray-900 text-white border border-gray-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none"
              >
                <option value="">{boutiques.length === 1 ? `${boutiques[0].nom} (automatique)` : '— Choisir une boutique —'}</option>
                {boutiques.map(b => (
                  <option key={b.id} value={b.id}>{b.nom}{b.ville ? ` — ${b.ville}` : ''}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Un gérant/vendeur opère dans une seule boutique.
              </p>
            </div>
          )}
          {form.role === 'proprietaire' && boutiques.length > 1 && (
            <p className="text-xs text-emerald-400/80 bg-emerald-900/20 border border-emerald-700/40 rounded-lg px-3 py-2">
              Le propriétaire a automatiquement accès à toutes les boutiques ({boutiques.length}).
            </p>
          )}

          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={saving} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
              {saving ? 'Création…' : 'Créer'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg text-sm border hover:bg-gray-800">
              Annuler
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-400">Chargement…</div>
      ) : (
        <div className="bg-gray-900 rounded-xl border overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-xs text-gray-500 border-b bg-gray-800">
                <th className="px-4 py-3 text-left">Nom</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Rôle</th>
                <th className="px-4 py-3 text-left">Boutique</th>
                <th className="px-4 py-3 text-center">Statut</th>
                <th className="px-4 py-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b hover:bg-gray-800">
                  <td className="px-4 py-3 font-medium text-gray-100">
                    <div className="flex items-center gap-2.5">
                      {resolveUploadUrl(u.photo_url) ? (
                        <img
                          src={resolveUploadUrl(u.photo_url)}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover shrink-0 border border-gray-700"
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 bg-gray-700 text-gray-200">
                          {initiales(u.prenom, u.nom)}
                        </div>
                      )}
                      <span>{u.prenom} {u.nom}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{u.email}</td>
                  <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {u.boutique_nom
                      ? <>{u.boutique_nom}</>
                      : ['admin', 'proprietaire'].includes(u.role)
                        ? <span className="italic text-gray-500">Toutes les boutiques</span>
                        : <span className="italic text-gray-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${u.actif ? 'bg-green-100 text-green-400' : 'bg-gray-100 text-gray-500'}`}>
                      {u.actif ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {isAdmin && u.id === userConnecte?.id ? (
                      // Son propre compte : pas d'auto-modification (règle
                      // appliquée aussi côté backend). Le mot de passe se
                      // change via « Changer mon mot de passe ».
                      <div className="flex items-center justify-center">
                        <span className="text-xs text-gray-500 italic px-2 py-1 bg-gray-800 border border-gray-700 rounded-md"
                          title="Vous ne pouvez pas modifier votre propre compte. Demandez à un autre administrateur.">
                          Vous (non modifiable)
                        </span>
                      </div>
                    ) : isAdmin ? (
                      <div className="flex items-center justify-center gap-1.5 flex-wrap">
                        <button
                          onClick={() => setUserEdition(u)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-indigo-400 bg-indigo-900/30 hover:bg-indigo-800/40 border border-indigo-700/50 rounded-md transition-colors"
                          title="Modifier les informations"
                        >
                          <Edit size={11} /> Modifier
                        </button>
                        <button
                          onClick={() => setUserResetMdp(u)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-amber-400 bg-amber-900/20 hover:bg-amber-800/40 border border-amber-700/50 rounded-md transition-colors"
                          title="Réinitialiser le mot de passe"
                        >
                          <KeyRound size={11} /> Mot de passe
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Réinitialiser la double authentification (2FA) de ${u.prenom} ${u.nom} ?\n\nÀ utiliser s'il a perdu son téléphone. Il pourra ensuite se reconnecter avec son mot de passe seul.`)) return;
                            try {
                              await api.post(`/users/${u.id}/reset-2fa`);
                              toast.success('Double authentification réinitialisée');
                            } catch (e) {
                              toast.error(e.response?.data?.message || 'Échec');
                            }
                          }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-purple-400 bg-purple-900/20 hover:bg-purple-800/40 border border-purple-700/50 rounded-md transition-colors"
                          title="Réinitialiser la double authentification (perte du téléphone)"
                        >
                          <KeyRound size={11} /> 2FA
                        </button>
                        <button
                          onClick={() => handleToggle(u)}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs border rounded-md transition-colors ${
                            u.actif
                              ? 'text-red-400 bg-red-900/20 hover:bg-red-800/40 border-red-700/50'
                              : 'text-green-400 bg-green-900/20 hover:bg-green-800/40 border-green-700/50'
                          }`}
                          title={u.actif ? 'Désactiver le compte' : 'Réactiver le compte'}
                        >
                          {u.actif ? 'Désactiver' : 'Réactiver'}
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 block text-center">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal — modifier les infos d'un utilisateur */}
      {userEdition && (
        <ModalEditerUtilisateur
          utilisateur={userEdition}
          boutiques={boutiques}
          onClose={() => setUserEdition(null)}
          onSuccess={() => {
            setUserEdition(null);
            setAlert({ type: 'success', msg: 'Utilisateur modifié avec succès' });
            load();
          }}
        />
      )}

      {/* Modal — réinitialiser le mot de passe d'un utilisateur */}
      {userResetMdp && (
        <ModalResetMotDePasse
          utilisateur={userResetMdp}
          onClose={() => setUserResetMdp(null)}
          onSuccess={(msg) => {
            setUserResetMdp(null);
            setAlert({ type: 'success', msg });
          }}
        />
      )}

      {/* Modal — confirmation désactivation / réactivation */}
      {confirmToggle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-gray-900 rounded-2xl w-full max-w-md p-6 shadow-2xl">
            <h2 className="text-lg font-bold text-white mb-3">
              {confirmToggle.actif ? 'Désactiver cet utilisateur ?' : 'Réactiver cet utilisateur ?'}
            </h2>
            <p className="text-sm text-gray-300 mb-4">
              {confirmToggle.actif ? (
                <>
                  Tu vas <strong>désactiver</strong> le compte de{' '}
                  <strong>{confirmToggle.prenom} {confirmToggle.nom}</strong> ({confirmToggle.email}).
                  <span className="block mt-2 text-xs text-gray-500">
                    L'utilisateur ne pourra plus se connecter. Le compte reste en base
                    (toutes ses actions passées restent traçables). Tu pourras le réactiver à tout moment.
                  </span>
                </>
              ) : (
                <>
                  Tu vas <strong>réactiver</strong> le compte de{' '}
                  <strong>{confirmToggle.prenom} {confirmToggle.nom}</strong> ({confirmToggle.email}).
                  <span className="block mt-2 text-xs text-gray-500">
                    L'utilisateur pourra de nouveau se connecter avec son mot de passe existant.
                  </span>
                </>
              )}
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setConfirmToggle(null)}
                className="px-4 py-2 rounded-lg text-sm border hover:bg-gray-800"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={confirmerToggle}
                className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${
                  confirmToggle.actif ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {confirmToggle.actif ? 'Oui, désactiver' : 'Oui, réactiver'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section : axes de variantes configurables ────────────────
// Chaque commerce peut renommer ou masquer les 3 axes de ses variantes
// (modèle/type, taille/pointure/dosage, couleur/parfum…). Stocké dans
// la table parametres (clés axe_*), lu par les formulaires produits.
// Éditeur des caractéristiques de variation d'UNE catégorie.
// 0 caractéristique = produits simples ; 1-2 = variantes structurées.
function EditeurCaracteristiques({ cat, onSaved, showAlert }) {
  // État local : liste d'attributs { nom, valeursText } (valeurs séparées par virgule)
  const init = () => (cat.attributs || []).map(a => ({
    nom: a.nom,
    valeursText: (a.valeurs_suggerees || []).join(', '),
  }));
  const [attrs, setAttrs] = useState(init);
  const [saving, setSaving] = useState(false);

  const ajouter = () => {
    if (attrs.length >= 2) return showAlert('error', 'Maximum 2 caractéristiques par catégorie');
    setAttrs([...attrs, { nom: '', valeursText: '' }]);
  };
  const maj = (i, champ, val) => setAttrs(attrs.map((a, idx) => idx === i ? { ...a, [champ]: val } : a));
  const retirer = (i) => setAttrs(attrs.filter((_, idx) => idx !== i));

  const sauvegarder = async () => {
    const payload = attrs
      .filter(a => a.nom.trim())
      .map(a => ({
        nom: a.nom.trim(),
        valeurs_suggerees: a.valeursText.split(',').map(v => v.trim()).filter(Boolean),
      }));
    setSaving(true);
    try {
      await api.put(`/categories/${cat.id}/attributs`, { attributs: payload });
      showAlert('success', payload.length === 0
        ? 'Catégorie en « produit simple »'
        : 'Caractéristiques enregistrées');
      onSaved();
    } catch (err) {
      showAlert('error', err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <h4 className="text-sm font-semibold text-gray-300">Caractéristiques de variation</h4>
        <p className="text-xs text-gray-400 mt-0.5">
          Qu'est-ce qui change d'un produit à l'autre dans cette catégorie ? (ex : Diamètre, Capacité, Couleur).
          Laisse vide pour un <strong>produit simple</strong> (un seul prix, pas de variante).
        </p>
      </div>

      {attrs.length === 0 && (
        <p className="text-xs text-gray-500 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2">
          📦 Produit simple : les produits de cette catégorie auront juste un nom et un prix.
        </p>
      )}

      {attrs.map((a, i) => (
        <div key={i} className="bg-gray-900 border border-gray-700 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2">
            <input
              value={a.nom}
              onChange={e => maj(i, 'nom', e.target.value)}
              placeholder={i === 0 ? 'Ex : Diamètre' : 'Ex : Couleur'}
              className="flex-1 bg-gray-800 text-white border border-gray-700 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
            />
            <button onClick={() => retirer(i)} className="text-red-400 hover:text-red-300 text-sm px-2" title="Retirer">×</button>
          </div>
          <input
            value={a.valeursText}
            onChange={e => maj(i, 'valeursText', e.target.value)}
            placeholder="Valeurs suggérées, séparées par des virgules : 8mm, 10mm, 12mm"
            className="w-full bg-gray-800 text-gray-300 border border-gray-700 rounded-lg px-3 py-1.5 text-xs focus:ring-2 focus:ring-indigo-400 outline-none"
          />
        </div>
      ))}

      <div className="flex items-center gap-2">
        {attrs.length < 2 && (
          <button onClick={ajouter} className="text-indigo-400 hover:text-indigo-300 text-sm flex items-center gap-1">
            + Ajouter une caractéristique
          </button>
        )}
        <button onClick={sauvegarder} disabled={saving}
          className="ml-auto bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
}

// ── Onglet Catégories & Tailles ───────────────────────────────
function OngletCatalogues() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ type: '', msg: '' });
  const [expanded, setExpanded] = useState(null);
  const [editingCat, setEditingCat] = useState(null); // { id, nom, emoji, modeles[] }
  const [newCat, setNewCat] = useState({ nom: '', emoji: '📦' });
  const [showNewCat, setShowNewCat] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadCats = () => {
    api.get('/categories')
      .then(r => setCategories(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadCats(); }, []);

  const showAlert = (type, msg) => {
    setAlert({ type, msg });
    setTimeout(() => setAlert({ type: '', msg: '' }), 3000);
  };

  /* ── Catégories ── */
  const createCategorie = async () => {
    if (!newCat.nom.trim()) return showAlert('error', 'Nom requis');
    setSaving(true);
    try {
      await api.post('/categories', { nom: newCat.nom.trim(), emoji: newCat.emoji, modeles: [] });
      setNewCat({ nom: '', emoji: '📦' });
      setShowNewCat(false);
      loadCats();
      showAlert('success', 'Catégorie créée');
    } catch (err) {
      showAlert('error', err.response?.data?.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const saveCategorie = async () => {
    if (!editingCat) return;
    try {
      await api.put(`/categories/${editingCat.id}`, {
        nom: editingCat.nom,
        emoji: editingCat.emoji,
        modeles: editingCat.modeles,
      });
      setEditingCat(null);
      loadCats();
      showAlert('success', 'Catégorie mise à jour');
    } catch (err) {
      showAlert('error', err.response?.data?.message || 'Erreur');
    }
  };

  const deleteCategorie = async (id) => {
    if (!window.confirm('Désactiver cette catégorie ? Les produits associés ne seront plus visibles.')) return;
    try {
      await api.delete(`/categories/${id}`);
      if (expanded === id) setExpanded(null);
      loadCats();
      // Toast (toujours visible, même si la liste est longue / défilée)
      toast.success('Catégorie désactivée');
      showAlert('success', 'Catégorie désactivée');
    } catch (err) {
      // 409 = la catégorie contient encore des produits actifs → message clair
      const msg = err.response?.data?.message
        || 'Impossible : des produits actifs utilisent cette catégorie. Désactivez-les d\'abord.';
      toast.error(msg, { duration: 6000 });
      showAlert('error', msg);
    }
  };





  if (loading) return <div className="text-center py-12 text-gray-400">Chargement…</div>;

  return (
    <div className="space-y-4 max-w-2xl">
      <Alert type={alert.type} msg={alert.msg} />

      {/* En-tête + bouton nouveau */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-100">Catégories de produits</h3>
          <p className="text-xs text-gray-400 mt-0.5">{categories.length} catégorie(s) — chaque catégorie définit ce qui « varie » pour ses produits</p>
        </div>
        <button
          onClick={() => setShowNewCat(!showNewCat)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition"
        >
          + Nouvelle catégorie
        </button>
      </div>

      {/* Formulaire nouvelle catégorie */}
      {showNewCat && (
        <div className="bg-indigo-900/30 border border-indigo-700/50 rounded-xl p-4 space-y-3">
          <h4 className="text-sm font-semibold text-indigo-300">Créer une nouvelle catégorie</h4>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Nom de la catégorie *</label>
            <input
              value={newCat.nom}
              onChange={e => setNewCat(p => ({ ...p, nom: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && createCategorie()}
              placeholder="Ex: Meubles, Électronique, Accessoires…"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-indigo-400 outline-none"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={createCategorie}
              disabled={saving}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Création…' : 'Créer la catégorie'}
            </button>
            <button
              onClick={() => { setShowNewCat(false); setNewCat({ nom: '', emoji: '📦' }); }}
              className="px-4 py-2 rounded-lg text-sm border hover:bg-gray-800"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Liste des catégories */}
      {categories.length === 0 ? (
        <div className="text-center py-10 border-2 border-dashed border-gray-700 rounded-xl">
          <p className="text-gray-400 text-sm">Aucune catégorie. Créez votre première catégorie ci-dessus.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {categories.map(cat => (
            <div key={cat.id} className="border rounded-xl overflow-hidden">
              {/* En-tête de la catégorie */}
              <div className="flex items-center justify-between p-4 bg-gray-900 hover:bg-gray-800 transition cursor-pointer"
                onClick={() => setExpanded(expanded === cat.id ? null : cat.id)}>
                <div className="flex items-center gap-3">
                  <div>
                    <p className="font-semibold text-gray-100">{cat.nom.charAt(0).toUpperCase() + cat.nom.slice(1)}</p>
                    <p className="text-xs text-gray-400">
                      {(cat.attributs || []).length === 0
                        ? 'Produit simple (sans variante)'
                        : `Varie par : ${cat.attributs.map(a => a.nom).join(', ')}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={e => { e.stopPropagation(); setEditingCat({ ...cat, modeles: cat.modeles || [] }); setExpanded(cat.id); }}
                    className="px-3 py-1.5 text-xs text-indigo-600 border border-indigo-700/50 rounded-lg hover:bg-indigo-900/30 transition"
                  >
                    ✏️ Modifier
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); deleteCategorie(cat.id); }}
                    className="px-3 py-1.5 text-xs text-red-500 border border-red-700/50 rounded-lg hover:bg-red-900/20 transition"
                  >
                    🗑️
                  </button>
                  <span className="text-gray-400 text-sm ml-1">{expanded === cat.id ? '▲' : '▼'}</span>
                </div>
              </div>

              {/* Panneau développé */}
              {expanded === cat.id && (
                <div className="border-t bg-gray-800 p-4 space-y-5">

                  {/* Mode édition du nom */}
                  {editingCat?.id === cat.id && (
                    <div className="bg-indigo-900/30 border border-indigo-700/50 rounded-xl p-4 space-y-3">
                      <h4 className="text-sm font-semibold text-indigo-300">Modifier la catégorie</h4>
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">Nom</label>
                        <input
                          value={editingCat.nom}
                          onChange={e => setEditingCat(p => ({ ...p, nom: e.target.value }))}
                          className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-indigo-400 outline-none"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={saveCategorie} className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700">
                          Enregistrer
                        </button>
                        <button onClick={() => setEditingCat(null)} className="px-4 py-2 rounded-lg text-sm border hover:bg-gray-700">
                          Annuler
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Caractéristiques de variation */}
                  <EditeurCaracteristiques cat={cat} onSaved={loadCats} showAlert={showAlert} />

                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────
export default function Parametres() {
  const [onglet, setOnglet] = useState('entreprise');

  const onglets = [
    { id: 'entreprise', label: 'Entreprise' },
    { id: 'utilisateurs', label: '👥 Utilisateurs' },
    { id: 'catalogues', label: '🏷️ Catégories & Tailles' },
  ];

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Paramètres</h1>
        <p className="text-gray-500 text-sm mt-1">Configuration de l'application</p>
      </div>

      {/* Onglets — harmonisés avec les autres boutons (indigo plein) */}
      <div className="flex gap-1 bg-gray-800 p-1 rounded-xl w-fit flex-wrap">
        {onglets.map(o => (
          <button
            key={o.id}
            onClick={() => setOnglet(o.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              onglet === o.id
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>

      {/* Contenu */}
      <div className="bg-gray-900 rounded-xl border p-5 md:p-6">
        {onglet === 'entreprise'   && <OngletEntreprise />}
        {onglet === 'utilisateurs' && <OngletUtilisateurs />}
        {onglet === 'catalogues'   && <OngletCatalogues />}
      </div>
    </div>
  );
}
